import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Snackbar,
    Stack
} from "@mui/material";
import { Replay, Refresh, ContentCopy, Download } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getGetProcessInstanceIncidentsQueryOptions, useRetryIncident, useGetIncidentStackTrace } from "../../../../../api/bpm/endpoints";

export const Route = createFileRoute('/__auth/processes/$processInstanceId/history/incidents')({
    component: IncidentsPage,
    staticData: {
        title: "routes.processIncidents",
    },
});

function IncidentsPage() {
    const { processInstanceId } = Route.useParams();
    const { t } = useTranslation();

    const { data: incidentsResponse, isLoading, error, refetch, isFetching } = useQuery({
        ...getGetProcessInstanceIncidentsQueryOptions(processInstanceId),
        enabled: !!processInstanceId
    });

    const retryIncidentMutation = useRetryIncident();
    const isMutationPending = retryIncidentMutation.isPending || (retryIncidentMutation as any).isLoading;

    const [retryDialogOpen, setRetryDialogOpen] = useState(false);
    const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);

    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
        open: false,
        message: "",
        severity: "success"
    });

    const incidents = (incidentsResponse as any)?.data || incidentsResponse || [];

    const formatDateTime = (dateString?: string) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit"
        });
    };

    const handleOpenRetry = (incidentId: string) => {
        setSelectedIncidentId(incidentId);
        setRetryDialogOpen(true);
    };

    const handleCloseRetry = () => {
        if (isMutationPending) return;
        setRetryDialogOpen(false);
        setSelectedIncidentId(null);
    };

    const handleConfirmRetry = () => {
        if (!selectedIncidentId) return;

        retryIncidentMutation.mutate({
            processInstanceId,
            incidentId: selectedIncidentId
        }, {
            onSuccess: () => {
                setSnackbar({
                    open: true,
                    message: t("processes.history.retrySuccess"),
                    severity: "success"
                });
                refetch();
                handleCloseRetry();
            },
            onError: () => {
                setSnackbar({
                    open: true,
                    message: t("processes.history.retryError"),
                    severity: "error"
                });
                handleCloseRetry();
            }
        });
    };

    const [stackTraceDialogOpen, setStackTraceDialogOpen] = useState(false);
    const [stackTraceText, setStackTraceText] = useState("");
    const stackTraceMutation = useGetIncidentStackTrace();

    const handleOpenStackTrace = (incidentId: string) => {
        setSelectedIncidentId(incidentId);
        setStackTraceText("");
        setStackTraceDialogOpen(true);
        stackTraceMutation.mutate({
            processInstanceId,
            incidentId
        }, {
            onSuccess: (result: any) => {
                const text = result?.data || result || "";
                setStackTraceText(text);
            },
            onError: () => {
                setSnackbar({
                    open: true,
                    message: t("processes.history.stackTraceError") || "Erro ao carregar stacktrace.",
                    severity: "error"
                });
                setStackTraceDialogOpen(false);
                setSelectedIncidentId(null);
            }
        });
    };

    const handleCloseStackTrace = () => {
        setStackTraceDialogOpen(false);
        setStackTraceText("");
        setSelectedIncidentId(null);
    };

    const handleCopyStackTrace = () => {
        navigator.clipboard.writeText(stackTraceText).then(() => {
            setSnackbar({
                open: true,
                message: t("processes.history.copySuccess") || "Copiado com sucesso!",
                severity: "success"
            });
        });
    };

    const handleDownloadStackTrace = () => {
        const blob = new Blob([stackTraceText], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `incident-${selectedIncidentId || "stacktrace"}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">{t("processes.history.errorLoadingIncidents")}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>
                    {t("processes.history.incidentsTitle")}
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={isFetching ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                    onClick={() => refetch()}
                    disabled={isFetching || isLoading}
                >
                    {t("processes.history.reload") || "Recarregar"}
                </Button>
            </Stack>

            {incidents.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {t("processes.history.noIncidentsFound")}
                </Alert>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Table sx={{ minWidth: 650 }} aria-label="incidents table">
                        <TableHead sx={{ bgcolor: 'action.hover' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: 200 }}>
                                    {t("processes.history.incidentTimestamp")}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t("processes.history.incidentMessage")}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', width: 100 }}>
                                    {t("common.actions")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {incidents.map((incident: any, index: number) => (
                                <TableRow key={incident.id || index}>
                                    <TableCell>
                                        {formatDateTime(incident.incidentTimestamp)}
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            color: 'error.main', 
                                            fontWeight: 500, 
                                            wordBreak: 'break-all',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                        onClick={() => handleOpenStackTrace(incident.id || "")}
                                    >
                                        {incident.incidentMessage || "Sem mensagem de erro."}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleOpenRetry(incident.id || "")}
                                            title={t("processes.history.retryIncident")}
                                            disabled={isMutationPending}
                                        >
                                            <Replay />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog 
                open={stackTraceDialogOpen} 
                onClose={handleCloseStackTrace} 
                fullWidth 
                maxWidth="lg"
            >
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t("processes.history.stackTraceTitle") || "StackTrace do Incidente"}</span>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ContentCopy />}
                            onClick={handleCopyStackTrace}
                            disabled={!stackTraceText || stackTraceMutation.isPending}
                        >
                            {t("common.copy") || "Copiar"}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Download />}
                            onClick={handleDownloadStackTrace}
                            disabled={!stackTraceText || stackTraceMutation.isPending}
                        >
                            {t("processes.history.downloadTxt") || "Baixar .txt"}
                        </Button>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {stackTraceMutation.isPending ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box 
                            component="pre" 
                            sx={{ 
                                m: 0, 
                                p: 2, 
                                bgcolor: 'action.hover', 
                                borderRadius: 1, 
                                overflowX: 'auto',
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                                maxHeight: '60vh'
                            }}
                        >
                            {stackTraceText || t("processes.history.noStackTrace") || "Nenhuma stacktrace disponível."}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseStackTrace} variant="contained" color="primary">
                        {t("common.close") || "Fechar"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={retryDialogOpen} onClose={handleCloseRetry}>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {t("processes.history.retryConfirmTitle")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("processes.history.retryConfirmMessage")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseRetry} variant="outlined" disabled={isMutationPending}>
                        {t("common.cancel")}
                    </Button>
                    <Button onClick={handleConfirmRetry} variant="contained" color="primary" autoFocus disabled={isMutationPending}>
                        {t("common.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
